import * as angular from 'angular';
import * as _ from 'lodash';
import { format } from 'd3';

import { ComponentDefinition, QualityWidgetType, TagDefinition, ConnectionPoint, BadActor, WidgetQualityResult } from '../../../models';
import { QualityBadActorsWidget } from './qualityBadActors.widget';
import { tagDefaultState, calculatedDefaultState, infoTextColor } from '../../../common/constants';
import { ImissingInputService } from '../../../services';
import { ICategoricalChartOptions } from '../../../wtFramework';
import { SingleValueSeriesFormat } from '../../../framework/models/SingleValueSeriesFormat';
import { connectionPointSelected } from '../../../common/events';

class QualityBadActorsController implements angular.IComponentController {
    data: BadActor[];
    id: string;
    options: ICategoricalChartOptions<BadActor, BadActor>;
    series: (BadActor & SingleValueSeriesFormat)[];

    readonly boundData: { results: WidgetQualityResult, range: { beginDateTime: string, endDateTime: string; } };
    readonly connectionPointsDictionary: { [guid: string]: ConnectionPoint };
    readonly item: QualityBadActorsWidget;
    readonly tagsDictionary: { [guid: string]: TagDefinition };

    private _clickCallback: (d: { guid: string | number }, i: number) => void;
    private _formatter: (n: number) => string;

    static $inject = ['$scope', '$state', '$rootScope', 'missingInputService'];
    constructor(private _$scope: angular.IScope,
        private _$state: angular.ui.IStateService,
        private _$rootScope: angular.IRootScopeService,
        private _missingInputService: ImissingInputService) {
    }

    $onInit() {
        this.id = `id${this.item.guid}`;
        let redirectCallback: (d, i) => void;
        switch (this.item.configuration.qualityWidgetType) {
            case QualityWidgetType.MissingTags:
                this._$scope.$on(connectionPointSelected, (_event, guid: string) => {
                    this._missingInputService.getCountPerTagForConnectionPoint(guid)
                        .then(dic => {
                            const dictionary = this.tagsDictionary;
                            const dataDic = dic;
                            const res = this.mapFromDictionary(dataDic, dictionary);
                            const data = res.data.sort((a, b) => a.value < b.value ? 1 : -1);
                            this.data = data;
                            this.series = data.map(d => _.find(res.series, s => s.guid === d.guid));
                        });
                });
                redirectCallback = (d) => this._$state.go(tagDefaultState, { tagDefinitionGuid: d.guid, beginDate: this.boundData.range.beginDateTime, endDate: this.boundData.range.endDateTime });
                break;
            case QualityWidgetType.BadValuesMostFrequent:
            case QualityWidgetType.FlatTags:
            case QualityWidgetType.OutOfRangeTags:
            case QualityWidgetType.ScatteredTags:
                redirectCallback = (d) => this._$state.go(tagDefaultState, { tagDefinitionGuid: d.guid, beginDate: this.boundData.range.beginDateTime, endDate: this.boundData.range.endDateTime });
                break;
            case QualityWidgetType.SuspiciousKPI:
            case QualityWidgetType.InvalidKPI:
                redirectCallback = (d) => this._$state.go(calculatedDefaultState, { identifierGuid: d.guid, beginDate: this.boundData.range.beginDateTime, endDate: this.boundData.range.endDateTime });
                break;
            case QualityWidgetType.MissingTagsPerConnectionPoint:
                redirectCallback = (d) => this._$rootScope.$broadcast(connectionPointSelected, d.guid);
                break;
        }
        this._clickCallback = redirectCallback;
        const formatStr = this.item.configuration.qualityWidgetType === QualityWidgetType.PercentageOfBadValuesByType
            ? '2.2%'
            : '0';
        const fmt = format(formatStr);
        this._formatter = fmt;
        // new bar chart
        this.options = {
            color: infoTextColor,
            dataFormat: {
                identifier: d => d.guid,
                label: d => d.name,
                value: d => d.value
            },
            isLabelsVisible: false,
            isTitleVisible: false,
            isRectangleVisible: true,
            isVerticalLineVisible: false,
            tooltip: {
                isVisible: true,
                format: (d, v) => `${d.name}: ${this._formatter(d.value)}`
            },
            minChartWidth: 50, //in px
            xTickFormat: formatStr
        };
        this.series = this.series || [];
        this.data = this.data || [];
    }

    $onChanges(changesObj: angular.IOnChangesObject) {
        if (changesObj['boundData'] && changesObj['boundData'].currentValue) {
            if (this.boundData && this.boundData.results) {
                let series: (BadActor & SingleValueSeriesFormat)[] = [];
                let data: BadActor[] = [];
                const { results } = this.boundData;
                if (results.countPerConnectionPoint && this.connectionPointsDictionary) {
                    const dictionary = this.connectionPointsDictionary;
                    const dataDic = results.countPerConnectionPoint;
                    const res = this.mapFromDictionary(dataDic, dictionary);
                    series = res.series;
                    data = res.data;
                } else if (results.countPerTag && this.tagsDictionary) {
                    const dictionary = this.tagsDictionary;
                    const dataDic = results.countPerTag;
                    const res = this.mapFromDictionary(dataDic, dictionary);
                    series = res.series;
                    data = res.data;
                } else if (results.badActors) {
                    data = results.badActors;
                    series = data.map((d, i) => {
                        return {
                            color: infoTextColor,
                            guid: d.guid,
                            name: d.name,
                            value: d.value,
                            clickCallback: () => {
                                if (this._clickCallback) {
                                    this._clickCallback(d, i);
                                }
                            },
                            format: d => this._formatter(d)
                        };
                    });
                } else if (results.errorDistributions) {
                    const labels = [
                        'OKWithErrors',
                        'OutOfRange',
                        'OverRange',
                        'UnderRange',
                        'Error',
                        'ServerError',
                        'NotFound',
                        'Unknown',
                        'NoData'
                    ];
                    labels.forEach((l, i) => {
                        const v = _.find(results.errorDistributions, d => d.name === l);
                        const toAdd = {
                            name: l,
                            status: i,
                            guid: l,
                            value: 0
                        };
                        if (v) {
                            toAdd.value = v.value;
                        }
                        data.push(toAdd);
                        series.push({
                            guid: l,
                            name: l,
                            value: toAdd.value,
                            color: infoTextColor,
                            format: d => this._formatter(d)
                        });
                    });
                }
                data = data.sort((a, b) => a.value < b.value ? 1 : -1);
                this.data = data;
                this.series = data.map(d => _.find(series, s => s.guid === d.guid));
            }
        }
    }

    private mapFromDictionary(dataDic: { [tagGuid: string]: number; }, dictionary: { [guid: string]: { guid: string, name: string }; }) {
        const series: (BadActor & SingleValueSeriesFormat)[] = [];
        const data: BadActor[] = [];
        for (const key of Object.keys(dataDic)) {
            const name = dictionary[key] ? dictionary[key].name : '??';
            const value = dataDic[key];
            series.push({
                guid: key,
                name,
                color: infoTextColor,
                value,
                clickCallback: () => {
                    if (this._clickCallback) {
                        this._clickCallback({ guid: key }, -1);
                    }
                },
                format: d => Math.round(d).toFixed(0)
            });
            data.push({ guid: key, value, name: '' });
        }
        return { series, data };
    }
}

export const wtQualityBadActors: ComponentDefinition = {
    componentId: 'wtQualityBadActors',
    controller: QualityBadActorsController,
    template: require('./qualityBadActors.component.html'),
    controllerAs: 'vm',
    bindings: {
        boundData: '<',
        connectionPointsDictionary: '<?',
        item: '<',
        tagsDictionary: '<?'
    }
};